import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { companyInfo } from '../mock/data';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About Us', path: '/about' },
    { name: 'Market Insights', path: '/insights' }
  ];

  const regulatoryLinks = [
    { name: 'Investor Charter', path: '/investor-charter' },
    { name: 'MITC', path: '/mitc' },
    { name: 'AML Policy', path: '/aml-policy' },
    { name: 'Grievance', path: '/grievance' },
    { name: 'Complaint Board', path: '/complaint-board' },
    { name: 'Accessibility', path: '/accessibility' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      role="banner"
      className={`fixed w-full top-9 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-950/95 backdrop-blur-md shadow-lg shadow-emerald-900/10 border-b border-amber-500/20'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 py-4" aria-label="Main navigation">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label={`${companyInfo.name} - Go to homepage`}>
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt={`${companyInfo.name} logo`}
                width="48"
                height="48"
                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </picture>
            <div>
              <span className="text-xl font-bold text-amber-400 block">
                {companyInfo.name}
              </span>
              <p className="text-xs text-gray-400">SEBI Reg: {companyInfo.sebiReg}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-200 relative group ${
                  isActive(link.path)
                    ? 'text-amber-400'
                    : 'text-gray-300 hover:text-amber-400'
                }`}
                aria-current={isActive(link.path) ? 'page' : undefined}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-amber-500 transition-all duration-300 group-hover:w-full ${
                    isActive(link.path) ? 'w-full' : ''
                  }`}
                  aria-hidden="true"
                />
              </Link>
            ))}

            {/* Regulatory Disclosures Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                    regulatoryLinks.some(link => isActive(link.path))
                      ? 'text-amber-400'
                      : 'text-gray-300 hover:text-amber-400'
                  }`}
                  data-testid="regulatory-disclosures-dropdown"
                  aria-label="Regulatory Disclosures menu"
                >
                  Regulatory Disclosures
                  <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-slate-900 border border-amber-500/20 min-w-[180px]"
              >
                {regulatoryLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link
                      to={link.path}
                      className={`w-full px-3 py-2 text-sm cursor-pointer transition-colors ${
                        isActive(link.path)
                          ? 'text-amber-400 bg-amber-500/10'
                          : 'text-gray-300 hover:text-amber-400 hover:bg-amber-500/10'
                      }`}
                      data-testid={`regulatory-link-${link.path.replace('/', '')}`}
                      aria-current={isActive(link.path) ? 'page' : undefined}
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Contact Link */}
            <Link
              to="/contact"
              className={`text-sm font-medium transition-all duration-200 relative group ${
                isActive('/contact')
                  ? 'text-amber-400'
                  : 'text-gray-300 hover:text-amber-400'
              }`}
              aria-current={isActive('/contact') ? 'page' : undefined}
            >
              Contact
              <span
                className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-amber-500 transition-all duration-300 group-hover:w-full ${
                  isActive('/contact') ? 'w-full' : ''
                }`}
                aria-hidden="true"
              />
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 hover:shadow-emerald-700/50 transition-all duration-300"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-300 hover:text-amber-400 transition-colors duration-200"
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-amber-500/20" id="mobile-navigation" role="navigation" aria-label="Mobile navigation">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(link.path)
                      ? 'text-amber-400 bg-amber-500/10'
                      : 'text-gray-300 hover:text-amber-400 hover:bg-amber-500/5'
                  }`}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                >
                  {link.name}
                </Link>
              ))}

              {/* Regulatory Disclosures Section */}
              <div className="border-t border-amber-500/20 pt-4 mt-2">
                <p className="px-4 text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2" id="mobile-regulatory-label">
                  Regulatory Disclosures
                </p>
                <div role="group" aria-labelledby="mobile-regulatory-label">
                  {regulatoryLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 block ${
                        isActive(link.path)
                          ? 'text-amber-400 bg-amber-500/10'
                          : 'text-gray-300 hover:text-amber-400 hover:bg-amber-500/5'
                      }`}
                      aria-current={isActive(link.path) ? 'page' : undefined}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact Link */}
              <div className="border-t border-amber-500/20 pt-4 mt-2">
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 block ${
                    isActive('/contact')
                      ? 'text-amber-400 bg-amber-500/10'
                      : 'text-gray-300 hover:text-amber-400 hover:bg-amber-500/5'
                  }`}
                  aria-current={isActive('/contact') ? 'page' : undefined}
                >
                  Contact
                </Link>
              </div>

              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="mt-2 px-6 py-2.5 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white font-semibold rounded-lg text-center shadow-lg shadow-emerald-900/50"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
